import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useRegisterFilters } from '@hooks';
import { FilterBar, TablePagination } from '@components';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import {
  anomalyHistoryFilters, reportHistoryFilters, historySearch, historyColumns, sortHistory,
  readHistoryDates, historyWithinDates,
} from '@/entities/history/history.model';
import styles from './HistoryRecords.module.css';

export default function HistoryRecords({ rows, kind, onOpen, selectedId }) {
  const [params, setParams] = useSearchParams();
  const prefix = 'history.' + kind;
  const dates = readHistoryDates(params, prefix);
  const columns = useMemo(() => historyColumns(kind), [kind]);
  const filters = useRegisterFilters({
    namespace: prefix, items: rows,
    definitions: kind === 'anomaly' ? anomalyHistoryFilters : reportHistoryFilters, getSearchText: historySearch,
  });
  const [sort, setSort] = useState({ key: 'day', direction: 'desc' });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ key: '', page: 0 });
  const filtered = filters.filtered.filter(row => historyWithinDates(row, dates));
  const sorted = sortHistory(filtered, sort, columns);
  const pageKey = JSON.stringify([filters.query, filters.values, dates, sort, rowsPerPage]);
  const page = pagination.key === pageKey ? Math.min(pagination.page, Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1)) : 0;
  const visible = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  function setDate(key, value) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (value) next.set(prefix + '.' + key, value); else next.delete(prefix + '.' + key);
      return next;
    }, { replace: true });
  }
  function clearDates() {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      for (const key of ['from', 'to', 'undated']) next.delete(prefix + '.' + key);
      return next;
    });
  }
  return <div className={styles.records}>
    <div className={styles.dates}>
      <label>From<input type="date" value={dates.from} onChange={event => setDate('from', event.target.value)} /></label>
      <label>Through<input type="date" value={dates.to} onChange={event => setDate('to', event.target.value)} /></label>
      <label className={styles.check}><input type="checkbox" checked={dates.includeUndated}
        onChange={event => setDate('undated', String(event.target.checked))} />Include records without usable dates</label>
      <button type="button" className={styles.link} onClick={clearDates}>Clear dates</button>
    </div>
    <p className={styles.note}>Dates use the calendar day written in each record, without timezone conversion.
      Limits include both end dates. Undated/invalid dates are listed last and excluded from a date range unless explicitly included.</p>
    {dates.error && <p role="alert" className={styles.notice}>{dates.error}</p>}
    <FilterBar filters={filters} noun={kind === 'anomaly' ? 'anomaly records before date limits' : 'reports before date limits'} />
    <p role="status" className={styles.note}>{sorted.length} record(s) after all filters and date limits.
      {' '}{rows.filter(row => !row.day).length} record(s) in this scope have no usable date.</p>
    {selectedId && !sorted.some(row => row.id === selectedId) && rows.some(row => row.id === selectedId) &&
      <p className={styles.notice}>The selected record is outside these filters; its detail remains open.</p>}
    <div className={styles.tableArea}>
      {visible.length ? <RegisterTable rows={visible} columns={columns} sort={sort} onSortChange={setSort}
        getRowKey={row => row.id} onOpen={onOpen} selectedKey={selectedId}
        caption={kind === 'anomaly' ? 'Anomaly records ordered by recorded date raised' : 'Report chronology'}
        renderCell={(row, column) => {
          if (column.key === 'reference') return <>{row.reference}<small>{kind === 'anomaly' ? 'Current record · not a transition event' : 'Report metadata · not inspection completion'}</small></>;
          if (column.key === 'day') return <span title={row.rawDate || 'Date not recorded'}>{row.day || 'No usable date'}</span>;
          if (column.key === 'location') return <>{row.assetLabel || row.assetId || 'Asset not recorded'}<small>{row.siteLabel || row.siteId || 'Site not recorded'}</small></>;
          if (column.key === 'currentStatus' || column.key === 'type') return <span className={styles.badge}>{column.value(row)}</span>;
          return column.value(row) || 'Not recorded';
        }} /> : <p className={styles.empty}>{dates.error ? 'Correct the date limits to display results.' :
          rows.length ? 'No records match these filters.' : 'No records are available in this scope.'}</p>}
    </div>
    <TablePagination count={sorted.length} page={page} rowsPerPage={rowsPerPage}
      onPageChange={(_event, next) => setPagination({ key: pageKey, page: next })}
      onRowsPerPageChange={event => setRowsPerPage(Number(event.target.value))} rowsPerPageOptions={[25, 50, 100]} />
  </div>;
}